import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import PdfLeadGate from "../components/PdfLeadGate";
import useSeoHead from "../hooks/useSeoHead";
import {
  ArrowRight, ArrowLeft, Loader2, ChevronDown, ChevronRight,
  CheckCircle2, Mail, ShieldCheck, Download,
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function SeoLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/landings/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  // Build SEO + JSON-LD payload (TechArticle + FAQ + Breadcrumb)
  const l = data?.landing;
  const seo = l?.seo || {};
  const pageUrl = l ? `${BASE}/landing/${l.slug}` : "";
  const techArticleLd = l ? {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    "headline": l.h1 || seo.title || "",
    "description": seo.description || l.tagline || "",
    "url": pageUrl,
    "datePublished": l.date_published || "2025-01-01",
    "dateModified": l.date_modified || new Date().toISOString().slice(0, 10),
    "inLanguage": "en",
    "isAccessibleForFree": true,
    "mainEntityOfPage": { "@type": "WebPage", "@id": pageUrl },
    "author": { "@type": "Organization", "name": "NIBRO Engineering", "url": "https://stainlesspipeclamps.store/" },
    "publisher": {
      "@type": "Organization",
      "name": "NIBRO by ARAMFIX",
      "logo": { "@type": "ImageObject", "url": `${BASE}/aramfix-logo.png` },
    },
    "about": (l.keywords_about || ["pipe clamps", "stainless steel", "DIN 11852", "EN 10357", "ASME-BPE"]).map(
      (k) => ({ "@type": "Thing", "name": k })
    ),
    "keywords": seo.keywords || "",
    "proficiencyLevel": "Expert",
  } : null;
  const faqLd = l?.faq?.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": l.faq.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  } : null;
  const breadcrumbLd = l ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": `${BASE}/` },
      { "@type": "ListItem", "position": 2, "name": "Industries", "item": `${BASE}/industries` },
      { "@type": "ListItem", "position": 3, "name": l.h1, "item": pageUrl },
    ],
  } : null;

  useSeoHead({
    title: l ? (seo.title || l.h1) : "",
    description: l ? (seo.description || l.tagline) : "",
    canonical: pageUrl,
    meta: l ? [
      ...(seo.keywords ? [{ name: "keywords", content: seo.keywords }] : []),
      { property: "og:title", content: seo.title || l.h1 },
      { property: "og:description", content: seo.description || l.tagline },
      { property: "og:type", content: "article" },
      { property: "og:url", content: pageUrl },
      { property: "article:section", content: l.category || "Engineering" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: seo.title || l.h1 },
      { name: "twitter:description", content: seo.description || l.tagline },
    ] : [],
    jsonLd: [techArticleLd, faqLd, breadcrumbLd].filter(Boolean),
  });

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="animate-spin text-nibro-sage-dark" />
      </div>
    );
  }

  if (!data || !data.landing) {
    return (
      <div className="mx-auto max-w-[1200px] px-5 py-20 text-center">
        <h2 className="font-display text-3xl">Page not found</h2>
        <Link to="/products" className="mt-6 inline-block text-nibro-sage-dark hover:underline">
          Browse products →
        </Link>
      </div>
    );
  }

  const products = data.products || [];

  return (
    <div className="min-h-screen bg-white" data-testid="seo-landing-page">

      {/* Hero */}
      <div className="bg-nibro-sage-pale">
        <div className="mx-auto max-w-[1400px] px-5 py-12 lg:px-10 lg:py-16">
          <Link
            to="/industries"
            className="mb-4 inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900"
          >
            <ArrowLeft size={14} /> All industries
          </Link>
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.5fr_1fr] lg:items-center">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-aramfix-red/30 bg-white px-3 py-1.5 shadow-sm">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-aramfix-red" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-700">
                  {l.category === "brand" ? "About ARAMFIX" : l.category === "service" ? "Engineering service" : "Industry solution"}
                </span>
              </div>
              <h1
                className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-zinc-900 sm:text-5xl lg:text-6xl"
                data-testid="seo-landing-h1"
              >
                {l.h1}
              </h1>
              {l.tagline && (
                <p className="mt-5 max-w-2xl text-lg leading-relaxed text-zinc-700">
                  {l.tagline}
                </p>
              )}
              {l.lead && (
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-zinc-600">
                  {l.lead}
                </p>
              )}
              {l.cta && (
                <div className="mt-7 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(l.cta.primary.to)}
                    className="flex items-center gap-2 rounded-md bg-nibro-sage px-6 py-3 text-sm font-bold text-white transition hover:bg-nibro-sage-hover"
                    data-testid="seo-landing-cta-primary"
                  >
                    {l.cta.primary.label} <ArrowRight size={14} />
                  </button>
                  {l.cta.secondary && (
                    <button
                      type="button"
                      onClick={() => navigate(l.cta.secondary.to)}
                      className="flex items-center gap-2 rounded-md border-2 border-nibro-sage bg-white px-6 py-3 text-sm font-bold text-nibro-sage-dark transition hover:bg-nibro-sage-pale"
                      data-testid="seo-landing-cta-secondary"
                    >
                      {l.cta.secondary.label}
                    </button>
                  )}
                  <PdfLeadGate
                    kind="landing"
                    slug={l.slug}
                    title={l.h1}
                    pdfUrl={`${BASE}/api/landings/${l.slug}/spec-sheet.pdf`}
                  >
                    {(onClick) => (
                      <a
                        href={`${BASE}/api/landings/${l.slug}/spec-sheet.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={onClick}
                        data-testid="landing-spec-sheet-hero"
                        className="flex items-center gap-2 rounded-md bg-aramfix-red px-6 py-3 text-sm font-bold uppercase tracking-wider text-white shadow-sm transition hover:bg-aramfix-red-hover"
                      >
                        <Download size={14} /> Spec sheet PDF
                      </a>
                    )}
                  </PdfLeadGate>
                </div>
              )}
            </div>
            <TrustBox />
          </div>
        </div>
      </div>

      {/* Sections */}
      {l.sections && l.sections.length > 0 && (
        <div className="mx-auto max-w-[1100px] px-5 py-14 lg:px-10">
          <div className="space-y-12">
            {l.sections.map((s, i) => (
              <section key={i} className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-aramfix-red">
                    Section {String(i + 1).padStart(2, "0")}
                  </div>
                  <h2 className="mt-2 font-display text-2xl font-bold text-zinc-900">
                    {s.title}
                  </h2>
                </div>
                <div className="space-y-3 text-base leading-relaxed text-zinc-700">
                  {(s.body || []).map((para, j) => (
                    <p key={j}>{para}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {/* In-flow spec-sheet lead magnet */}
          <div
            className="mt-14 flex flex-col items-start gap-5 rounded-xl border-2 border-dashed border-nibro-sage bg-nibro-sage/5 p-6 sm:flex-row sm:items-center sm:justify-between"
            data-testid="landing-spec-sheet-inline"
          >
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-nibro-sage-dark p-3 text-white">
                <Download size={22} />
              </div>
              <div>
                <h3 className="text-base font-bold text-zinc-900">
                  Take this spec sheet into your next design review
                </h3>
                <p className="mt-1 text-sm text-zinc-600">
                  NIBRO-branded A4 PDF — printable, with QR back-link and recommended products table.
                </p>
              </div>
            </div>
            <PdfLeadGate
              kind="landing"
              slug={l.slug}
              title={l.h1}
              pdfUrl={`${BASE}/api/landings/${l.slug}/spec-sheet.pdf`}
            >
              {(onClick) => (
                <a
                  href={`${BASE}/api/landings/${l.slug}/spec-sheet.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClick}
                  data-testid="landing-spec-sheet-button"
                  className="inline-flex flex-shrink-0 items-center gap-2 rounded-md bg-aramfix-red px-5 py-3 text-sm font-bold uppercase tracking-wider text-white shadow-sm transition hover:bg-aramfix-red-hover"
                >
                  <Download size={14} /> Download PDF
                </a>
              )}
            </PdfLeadGate>
          </div>
        </div>
      )}

      {/* Matching products */}
      {products.length > 0 && (
        <div className="border-t border-zinc-100 bg-zinc-50">
          <div className="mx-auto max-w-[1400px] px-5 py-12 lg:px-10">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <div className="label-uppercase">Matched products from stock</div>
                <h2 className="mt-1 font-display text-2xl font-bold">
                  {products.length} clamps that fit this application
                </h2>
              </div>
              <Link
                to="/products"
                className="hidden text-sm font-bold text-nibro-sage-dark hover:underline md:inline"
              >
                See all 1300+ products →
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {products.map((p) => <ProductCard key={p.id} p={p} />)}
            </div>
          </div>
        </div>
      )}

      {/* FAQ */}
      {l.faq && l.faq.length > 0 && (
        <div className="border-t border-zinc-100 bg-white">
          <div className="mx-auto max-w-[900px] px-5 py-14 lg:px-10">
            <div className="label-uppercase">FAQ</div>
            <h2 className="mt-2 font-display text-3xl font-bold text-zinc-900">
              Frequently asked
            </h2>
            <div className="mt-8 divide-y divide-zinc-200 border-y border-zinc-200">
              {l.faq.map((f, i) => {
                const isOpen = openFaq === i;
                return (
                  <div key={i}>
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? -1 : i)}
                      className="flex w-full items-start gap-3 py-4 text-left transition hover:bg-zinc-50"
                      data-testid={`faq-toggle-${i}`}
                    >
                      <span className="mt-0.5 flex-shrink-0 font-mono text-xs text-aramfix-red">
                        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </span>
                      <span className="font-display text-base font-bold text-zinc-900">
                        {f.q}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="pb-5 pl-7 pr-2 text-sm leading-relaxed text-zinc-700">
                        {f.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Final CTA */}
      <div className="border-t border-zinc-100 bg-nibro-sage py-12">
        <div className="mx-auto max-w-[900px] px-5 text-center text-white lg:px-10">
          <h2 className="font-display text-3xl font-bold">
            Ready to spec your supports?
          </h2>
          <p className="mt-3 text-white/85">
            Talk to our engineering team. Factory direct from Oisterwijk, Netherlands.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/contact"
              className="flex items-center gap-2 rounded-md bg-aramfix-red px-6 py-3 text-sm font-bold text-white hover:bg-aramfix-red/90"
              data-testid="seo-landing-final-cta"
            >
              <Mail size={14} /> Get a quote
            </Link>
            <Link
              to="/products"
              className="flex items-center gap-2 rounded-md border-2 border-white bg-transparent px-6 py-3 text-sm font-bold text-white hover:bg-white/10"
            >
              Browse stock <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function TrustBox() {
  const items = [
    { icon: ShieldCheck, label: "Factory direct since 2006" },
    { icon: ShieldCheck, label: "EU stainless · EN 10204 3.1 traceable" },
    { icon: ShieldCheck, label: "DIN 3567 + DIN 3570 compliant" },
    { icon: ShieldCheck, label: "ISO 9001:2015 certified production" },
    { icon: ShieldCheck, label: "Worldwide shipping in 22 countries" },
  ];
  return (
    <div className="rounded-lg border-2 border-aramfix-red/30 bg-white p-6 shadow-sm">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-aramfix-red">
        Why NIBRO
      </div>
      <div className="mt-3 space-y-2.5">
        {items.map((it, i) => {
          const Icon = it.icon;
          return (
            <div key={i} className="flex items-start gap-2.5">
              <Icon size={14} className="mt-0.5 flex-shrink-0 text-nibro-sage-dark" />
              <span className="text-sm leading-tight text-zinc-700">{it.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
